import { DataTable, DataTableColumn } from 'mantine-datatable';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  exitReasonLabel,
  formatEuros,
  formatExitDate,
} from 'src/Components/Inventory/inventoryLabels';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useSortableRecords from 'src/Hooks/Shared/useSortableRecords';
import { InventoryItemDto } from 'src/Services/API/generated';

import styles from './InventoryItemTable.module.scss';
import { Props } from './InventoryItemTable.types';

// Room by room by default (the inventory is done that way), objects without a room last.
const compareByRoom = (a: InventoryItemDto, b: InventoryItemDto): number => {
  if (!a.room && b.room) return 1;
  if (a.room && !b.room) return -1;
  const byRoom = (a.room?.name ?? '').localeCompare(b.room?.name ?? '', 'fr');
  return byRoom !== 0 ? byRoom : a.name.localeCompare(b.name, 'fr');
};

/** Objects of the house, as a table like the contacts and the tasks. */
const InventoryItemTable = ({
  items,
  fetching,
  noRecordsText,
  valuesVisible,
  exited,
  onItemClick,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const byRoom = useMemo(() => (items ? [...items].sort(compareByRoom) : undefined), [items]);
  const { sortedRecords, sortStatus, setSortStatus } = useSortableRecords(byRoom);

  const columns: DataTableColumn<InventoryItemDto>[] = [
    {
      accessor: 'name',
      title: t('inventory.table.name', 'Objet'),
      sortable: true,
      render: ({ name, quantity, brandAndModel, photos }) => (
        <div className={styles.nameCell}>
          {photos[0] ? (
            <img
              className={styles.thumb}
              src={photos[0].previewThumbnailUrl ?? photos[0].url}
              alt=""
            />
          ) : (
            <span className={styles.thumb} />
          )}
          <div className={styles.nameBlock}>
            <div className={styles.name}>{quantity > 1 ? `${name} (×${quantity})` : name}</div>
            {brandAndModel && <div className={styles.secondaryLine}>{brandAndModel}</div>}
          </div>
        </div>
      ),
    },
    {
      accessor: 'room.name',
      title: t('inventory.fields.room', 'Pièce'),
      sortable: true,
      hidden: isMobile,
      render: ({ room }) => (
        <div className={styles.value}>{room?.name ?? t('inventory.add.noRoom', 'Sans pièce')}</div>
      ),
    },
  ];

  if (exited) {
    columns.push({
      accessor: 'exitDate',
      title: t('inventory.table.exit', 'Sortie'),
      sortable: true,
      render: ({ exitReason, exitDate }) => (
        <div className={styles.value}>
          {[
            exitReason ? exitReasonLabel(t, exitReason) : null,
            exitDate ? formatExitDate(exitDate) : null,
          ]
            .filter(Boolean)
            .join(' · ')}
        </div>
      ),
    });
  } else if (valuesVisible) {
    columns.push({
      accessor: 'currentValue',
      title: t('inventory.table.currentValue', 'Valeur actuelle'),
      sortable: true,
      textAlign: 'right',
      render: ({ currentValue }) =>
        currentValue != null ? (
          <div className={styles.value}>{formatEuros(currentValue)}</div>
        ) : (
          <div className={styles.toComplete}>{t('inventory.toComplete', 'à compléter')}</div>
        ),
    });
  }

  return (
    <DataTable
      withRowBorders={false}
      style={{ width: '100%', minHeight: '10rem' }}
      records={sortedRecords}
      sortStatus={sortStatus}
      onSortStatusChange={setSortStatus}
      noRecordsText={noRecordsText}
      fetching={fetching}
      onRowClick={({ record }) => onItemClick(record)}
      columns={columns}
    />
  );
};

export default InventoryItemTable;
